import { useState } from "react";
import { categories, hotList, type Category } from "../../../mocks/news";

const heatRankColor = (rank: number) =>
  rank === 1
    ? "bg-primary-500 text-white"
    : rank === 2
      ? "bg-accent-500 text-white"
      : rank === 3
        ? "bg-accent-400 text-white"
        : "bg-background-200 text-foreground-600";

const deltaIcon: Record<string, string> = {
  up: "ri-arrow-up-line text-primary-600",
  down: "ri-arrow-down-line text-secondary-600",
  new: "ri-flashlight-fill text-accent-600",
  flat: "ri-drag-move-2-fill text-foreground-400",
};

const deltaLabel: Record<string, string> = {
  up: "上升",
  down: "下降",
  new: "新上",
  flat: "持平",
};

export default function HotBoard() {
  const [tab, setTab] = useState<Category | "全部">("全部");

  const list = hotList.filter((h) => tab === "全部" || h.category === tab);

  return (
    <section id="hot" className="mx-auto max-w-6xl scroll-mt-20 px-4 py-14 md:px-6 md:py-20">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold text-primary-600">
            <span className="h-px w-6 bg-primary-400" aria-hidden="true" />
            实时热度
          </p>
          <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            今日热搜榜
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-foreground-600">
            综合搜索热度与全网讨论量实时排序，热度越高代表越多人正在围观。点击分类即可聚焦你关心的瓜。
          </p>
        </div>
        <div className="flex flex-wrap gap-1 rounded-full bg-background-100 p-1 ring-1 ring-background-200">
          {(["全部", ...categories] as (Category | "全部")[]).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setTab(c)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                tab === c
                  ? "bg-primary-500 text-white"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-background-200 bg-background-100/50">
        <ol className="divide-y divide-background-200">
          {list.map((item) => {
            const maxDiscuss = Math.max(...list.map((l) => l.discuss));
            return (
              <li key={item.id}>
                <a
                  href="#latest"
                  className="group flex items-center gap-4 bg-background-50 px-4 py-4 transition-colors hover:bg-background-100 md:px-6"
                >
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-heading text-sm font-black ${heatRankColor(item.rank)}`}
                  >
                    {item.rank}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-foreground-900 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </p>
                      <span className="hidden rounded-full bg-secondary-50 px-2 py-0.5 text-xs text-secondary-700 sm:inline-flex">
                        {item.category}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                      <div className="h-1.5 w-full max-w-[200px] overflow-hidden rounded-full bg-background-200">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-primary-500 to-accent-500 transition-all"
                          style={{ width: `${(item.discuss / maxDiscuss) * 100}%` }}
                        />
                      </div>
                      <span className="hidden text-xs text-foreground-500 md:inline">
                        {item.discuss} 万人讨论
                      </span>
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="font-heading text-base font-bold tabular-nums text-foreground-800">
                      {item.heat}
                    </span>
                    <span
                      className={`flex items-center gap-0.5 text-xs ${item.delta === "up" || item.delta === "down" || item.delta === "new" ? "font-semibold" : ""}`}
                      style={{ color: "inherit" }}
                    >
                      <i className={`${deltaIcon[item.delta]} text-xs`} aria-hidden="true" />
                      <span className="text-foreground-500">{deltaLabel[item.delta]}</span>
                    </span>
                  </div>
                </a>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}